import React, { useState, useEffect, useRef, useCallback } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { DevisLine, BenchmarkStats, PriceVerdict } from '../types';
import { Ouvrage } from '../../1-bibliotheque-prix/data/ServicesData';
import { bestMatch } from '../utils/fuzzyMatch';
import { getPriceVerdict, computeEcart } from '../utils/benchmarkStats';
import { formatPrix } from '../utils/priceParsing';
import BenchmarkBadge from './BenchmarkBadge';
import OuvrageSearchDropdown from './OuvrageSearchDropdown';

interface DevisLineRowProps {
  line: DevisLine;
  ouvrages: Ouvrage[];
  benchmarkStats: BenchmarkStats | null;
  onUpdate: (updated: DevisLine) => void;
  onDelete: () => void;
}

const DevisLineRow: React.FC<DevisLineRowProps> = ({
  line,
  ouvrages,
  benchmarkStats,
  onUpdate,
  onDelete,
}) => {
  const [localPrestation, setLocalPrestation] = useState(line.prestation);
  const [showDropdown, setShowDropdown] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const matchChipRef = useRef<HTMLTableCellElement>(null);

  // Sync local prestation when line.prestation changes from outside
  useEffect(() => {
    setLocalPrestation(line.prestation);
  }, [line.prestation]);

  const runAutoMatch = useCallback(
    (text: string) => {
      if (text.trim().length < 2) return;
      const result = bestMatch(text, ouvrages);
      if (result) {
        onUpdate({
          ...line,
          prestation: text,
          matchedOuvrageId: result.ouvrage.id,
          matchConfidence: result.score,
          isManualMatch: false,
        });
      } else {
        onUpdate({
          ...line,
          prestation: text,
          matchedOuvrageId: null,
          matchConfidence: 0,
          isManualMatch: false,
        });
      }
    },
    [line, ouvrages, onUpdate]
  );

  const handlePrestationChange = (value: string) => {
    setLocalPrestation(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runAutoMatch(value), 300);
  };

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const handleManualSelect = (ouvrage: Ouvrage | null) => {
    onUpdate({
      ...line,
      matchedOuvrageId: ouvrage ? ouvrage.id : null,
      matchConfidence: ouvrage ? 1 : 0,
      isManualMatch: ouvrage !== null,
    });
  };

  const totalHT = line.quantite * line.prixUnitaire;
  const verdict: PriceVerdict = benchmarkStats
    ? getPriceVerdict(line.prixUnitaire, benchmarkStats)
    : 'no-data';
  const ecart =
    benchmarkStats && benchmarkStats.count > 0
      ? computeEcart(line.prixUnitaire, benchmarkStats.avg)
      : null;

  const matchedOuvrage = line.matchedOuvrageId
    ? ouvrages.find((o) => o.id === line.matchedOuvrageId)
    : null;

  const ecartColor =
    ecart === null
      ? 'text-gray-400'
      : ecart <= 5
        ? 'text-green-600'
        : ecart <= 20
          ? 'text-orange-600'
          : 'text-red-600';

  return (
    <tr className="border-b border-gray-100 hover:bg-gray-50/50 transition-colors">
      {/* Prestation */}
      <td className="px-3 py-3">
        <input
          type="text"
          value={localPrestation}
          onChange={(e) => handlePrestationChange(e.target.value)}
          placeholder="Ex: Cloison BA13..."
          className="w-full bg-transparent text-sm text-gray-900 font-medium placeholder-gray-300 outline-none border-b border-transparent focus:border-violet-400 transition-colors py-0.5"
        />
      </td>

      {/* Quantité */}
      <td className="px-3 py-3 w-20">
        <input
          type="number"
          min="0"
          step="any"
          value={line.quantite || ''}
          onChange={(e) =>
            onUpdate({ ...line, quantite: parseFloat(e.target.value) || 0 })
          }
          className="w-full bg-transparent text-sm text-gray-900 text-right outline-none border-b border-transparent focus:border-violet-400 transition-colors py-0.5"
        />
      </td>

      {/* Unité */}
      <td className="px-3 py-3 w-20">
        <input
          type="text"
          value={line.unite}
          onChange={(e) => onUpdate({ ...line, unite: e.target.value })}
          placeholder="m²"
          className="w-full bg-transparent text-sm text-gray-500 text-center outline-none border-b border-transparent focus:border-violet-400 transition-colors py-0.5"
        />
      </td>

      {/* Prix unitaire */}
      <td className="px-3 py-3 w-24">
        <input
          type="number"
          min="0"
          step="0.01"
          value={line.prixUnitaire || ''}
          onChange={(e) =>
            onUpdate({ ...line, prixUnitaire: parseFloat(e.target.value) || 0 })
          }
          className="w-full bg-transparent text-sm text-gray-900 text-right outline-none border-b border-transparent focus:border-violet-400 transition-colors py-0.5"
        />
      </td>

      {/* Total HT */}
      <td className="px-3 py-3 w-28 text-sm text-right font-medium text-gray-900">
        {totalHT > 0 ? `${formatPrix(totalHT)} €` : '—'}
      </td>

      {/* Correspondance */}
      <td className="px-3 py-3 relative" ref={matchChipRef}>
        {matchedOuvrage ? (
          <button
            onClick={() => setShowDropdown(!showDropdown)}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-violet-100 text-violet-700 hover:bg-violet-200 transition-colors max-w-[180px] truncate"
            title={matchedOuvrage.intitule}
          >
            {matchedOuvrage.intitule}
          </button>
        ) : (
          <button
            onClick={() => setShowDropdown(!showDropdown)}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 transition-colors"
          >
            Associer...
          </button>
        )}

        {showDropdown && (
          <OuvrageSearchDropdown
            ouvrages={ouvrages}
            selectedOuvrageId={line.matchedOuvrageId}
            onSelect={handleManualSelect}
            onClose={() => setShowDropdown(false)}
          />
        )}
      </td>

      {/* Benchmark */}
      <td className="px-3 py-3">
        <BenchmarkBadge stats={benchmarkStats} verdict={verdict} />
      </td>

      {/* Écart */}
      <td className={`px-3 py-3 text-sm text-right font-medium ${ecartColor}`}>
        {ecart !== null ? `${ecart >= 0 ? '+' : ''}${ecart.toFixed(1)}%` : '—'}
      </td>

      {/* Supprimer */}
      <td className="px-3 py-3 w-10">
        <button
          onClick={onDelete}
          className="p-1 text-gray-400 hover:text-red-500 transition-colors rounded"
          title="Supprimer cette ligne"
        >
          <XMarkIcon className="w-4 h-4" />
        </button>
      </td>
    </tr>
  );
};

export default DevisLineRow;
